/* eslint-disable no-shadow */
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import {
  Backdrop,
  Grid,
  CircularProgress,
  Button,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import Card from '@src/components/Card';
import { ORDER_STATE, ORDER_STATUS } from '@src/constants/order';
import ROUTES from '@src/constants/route';
import { checkVietNam } from '@src/utils/checkCountry';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import moment from 'moment';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import {
  getCustomPackagePrice,
  checkCustomPackage,
  getPackageChangeType,
  getPackageCurrentPrice,
  getPackageName,
} from '@src/services/package';
import { CRM_URL } from '@src/configs';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import { getSavedPrice, validateVoucherByRegion } from '@src/services/voucher';
import { getOrderPrice, getTotalPrice } from '@src/services/price';
import { validateEmail } from '@src/utils/checkValid';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
  PREFIX_AUTO_RENEW,
  TOP_UP_PACKAGE_CODES,
  PACKAGE_CODE_V3,
  TYPE_POPUP_MIGRATE,
  PACKAGE_CHANGE_TYPES,
} from '@src/constants/package';
import { BANNER_TYPE } from '@src/constants/banner';
import { VOUCHER_REGION, VOUCHER_TYPE } from '@src/constants/voucher';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import dataSenses from '@src/services/dataSenses';
import { getDataLocalStorage } from '@src/utils/localStorage';
import {
  getDefaultBank,
  getProductInfoForUserPackagePurchase,
} from '@src/services/payment';
import useIpLocation from '@src/hooks/useLocation';
import { getClickIdFromCookie, getOfferIdFromCookie } from '@src/utils/permate';
import {
  PERMATE_MARKETER_ID_KEY,
  PERMATE_OFFER_ID_KEY,
} from '@src/constants/permate';
import { min } from 'lodash';
import theme from '@src/styles/theme';
import { BREAKPOINTS } from '@src/styles/config';
import { DEFAULT_VOICE_CLONING_LIMIT_FOR_WHITELIST } from '@src/constants/voice';
import PackageInfo from './PackageInfo';

import OrderInfo from './OrderInfo';
import PaymentMethods from './PaymentMethods';
import PaymentFooter from './PaymentFooter';
import BankTransferDialogContainer from './Dialog/BankTransfer';
import QRPaymentDialog from './Dialog/QRPaymentDialog';
import PaymentNotiDialog from './Dialog/PaymentNotiDialog';
import SubscriptionInfo from './SubscriptionInfo';
import { StyledPayment } from './index.style';

const DIALOG_NAMES = {
  EXCEEDS_CHARACTERS: 'exceedsCharacters',
  CONFIRM_PAYMENT: 'confirmPayment',
  DOWNGRADE_PACKAGE: 'downgradePackage',
  UPGRADE_PACKAGE_OR_BUY_MORE: 'upgradePackageOrBuyMore',
  DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION:
    'downgradePackageAndCancelSubscription',
  CANCEL_SUBSCRIPTION: 'cancelSubscription',
  UNPAID_ORDER: 'unpaidOrder',
  NOT_CONFIRMED_ORDER: 'notConfirmedOrder', // Customer confirmed transfer, waiting for confirmation form customer service
  COMPLETED_ORDER: 'completedOrder',
  WARNING_EMAIL_ERROR: 'warningEmailError',
  INVALID_REGION_VOUCHER: 'invalidVoucherForRegion', // Global order cannout use voucher with type is PRICE
  BUY_SUBSCRIPTION_ON_APP: 'buySubscriptionOnApp', // Tutorial cancel subscription on app when last payment method is Apple or Google,
  UPGRADE_DOWNGRADE_PACK_V3: 'upgradeDowngradePackV3',
  VOICE_CLONING_QUOTA: 'voiceCloningQuota',
};

const Payment = () => {
  const location = useLocation();
  const [isVisiblePaymentDialog, setIsVisiblePaymentDialog] = useState(false);
  const { ip } = useIpLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));
  const isDubbingRoute = useCheckDubbingRoute();
  const [loading, setLoading] = useState(false);
  const [qrPaymentDialog, setQRPaymentDialog] = useState(null);
  const [bankTransferDialog, setBankTransferDialog] = useState(null);
  const [openDialog, setOpenDialog] = useState({});
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [showPendingOrder, setShowPendingOrder] = useState(true);
  const [usingOrders, setUsingOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackageCode, setSelectedPackageCode] = useState('');
  const [keyDescriptionUnPaidOrder, setKeyDescriptionUnPaidOrder] =
    useState('');
  const [currentNumberCharacters, setCurrentNumberCharacters] = useState(0);
  const [voiceCloningQuota, setVoiceCloningQuota] = useState({
    maxCloningVoices: 0,
    currentVoiceCloningCount: 0,
  });
  const [
    remainingValueOfTheCurrentPackage,
    setRemainingValueOfTheCurrentPackage,
  ] = useState({ price: 0, usdPrice: 0 });
  const { ttsUser } = useUserInfo();
  const {
    package: packageCode,
    autoRenew,
    credits: creditsQuery,
    devices: devicesQuery,
    voiceClonings: voiceCloningsQuery,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const isAutoRenew = useMemo(() => {
    if (autoRenew) {
      try {
        return JSON.parse(autoRenew);
      } catch (e) {
        return null;
      }
    }
    return false;
  }, [autoRenew]);

  const [productInfo, setProductInfo] = useState({
    total: null,
    expires: null,
    packageType: null,
  });

  const { user } = useSelector((state) => state.auth);
  const {
    latestOrder,
    usingPackage,
    backToTTS,
    closeQRPayment,
    backToDubbing,
    usingDubbingPackage,
    latestDubbingOrder,
    subscriptionPurchase,
  } = useSelector((state) => state.user);
  const { openSidebar } = useSelector((state) => state.layout);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isVietNam = checkVietNam();
  const [banks, setBanks] = useState([]);
  const isConfirmPaymentLocal = localStorage.getItem('confirmPaymentDialog');
  const fetchBanks = async () => {
    const data = await apis.banks.getTransferBanks();
    if (data?.status) setBanks(data?.result || []);
  };
  const { getFeatureValue } = useFeatureFlags();

  const useNewGlobalOrder =
    getFeatureValue(FEATURE_KEYS.NEW_GLOBAL_ORDER, {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    }) && !isVietNam;

  const usePermateTracking = getFeatureValue(FEATURE_KEYS.PERMATE_TRACKING);

  const useVoucherByRegion = getFeatureValue(FEATURE_KEYS.NEW_PRICE_VOUCHER, {
    ip,
  });

  const uiBuyTopupCharacters = getFeatureValue(
    FEATURE_KEYS.UI_BUY_TOP_UP_CHARACTERS,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const usePhoneCountryInput = getFeatureValue(
    FEATURE_KEYS.PHONE_COUNTRY_INPUT,
    {
      userId: user?.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useNewLegal = getFeatureValue(FEATURE_KEYS.NEW_LEGAL, {
    userId: user.id,
    phoneNumber: user.phoneNumber,
    email: user.email,
  });

  const bankDefaultValue = getFeatureValue(FEATURE_KEYS.DEFAULT_BANK, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useNewWarningBuyPackage = getFeatureValue(
    FEATURE_KEYS.NEW_WARNING_BUY_PACKAGE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const useNewPackageMobile = isMobile;
  const useNewPackagesTab = getFeatureValue(FEATURE_KEYS.NEW_PACKAGES_TAB, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const refundPriceCurrPlan = getFeatureValue(
    FEATURE_KEYS.REFUND_PRICE_CURRENT_PLAN,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const usePricingModelV3 = getFeatureValue(FEATURE_KEYS.NEW_PRICING_MODEL_V3, {
    ip,
  });

  const useMinPaymentMethodAmount = getFeatureValue(
    FEATURE_KEYS.USE_MIN_PAYMENT_METHOD_AMOUNT,
    {
      ip,
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const dataSensesFeatures = getFeatureValue(FEATURE_KEYS.DATASENSES);
  const sendDataSensesFromServer = dataSensesFeatures?.s2s;

  const isUIBuyTopupCharacters =
    uiBuyTopupCharacters && TOP_UP_PACKAGE_CODES.includes(packageCode);

  const isUsingCustomPackage = checkCustomPackage({
    type: usingPackage.type,
    level: usingPackage.level,
  });

  const {
    voucher,
    package: packageState,
    userOrder,
    paymentMethod,
    invoice,
    hasInvoice,
    customResource,
  } = useSelector((state) => state.order);
  const { price, usdPrice } = getPackageCurrentPrice(packageState, true);
  const { permateMarketerId, permateOfferId } = useSelector(
    (state) => state.user,
  );

  const isCustomPackage = checkCustomPackage({
    type: packageState.type,
    level: packageState.level,
  });

  const permateMarketerIdFromLocalStorage = getDataLocalStorage(
    PERMATE_MARKETER_ID_KEY,
  );
  const permateMarketerIdFromCookie = getClickIdFromCookie();

  const permateOfferIdFromLocalStorage =
    getDataLocalStorage(PERMATE_OFFER_ID_KEY);
  const permateOfferIdFromCookie = getOfferIdFromCookie();

  const dispatch = useDispatch();

  const isUpgradePackage = useMemo(() => {
    const determinePackageChange = () => {
      if (!usingPackage || !usingPackage?.cycle) return null;
      return getPackageChangeType({
        currPackage: packageState,
        prevPackage: usingPackage,
        prevPackageExpiredDate: ttsUser?.packageExpiryDate,
      });
    };
    return determinePackageChange() === PACKAGE_CHANGE_TYPES.UPGRADE;
  }, [usingPackage, packageState, ttsUser]);

  const handleChangeSelectedPackageCode = (value) =>
    setSelectedPackageCode(value);

  const handleCloseDialog = (dialogName) => {
    setOpenDialog({ ...openDialog, [dialogName]: false });
  };

  const handleOpenDialog = (dialogName) =>
    setOpenDialog({ ...openDialog, [dialogName]: true });

  const handleOpenConfirmDialog = ({
    user,
    packageState,
    voucher,
    dialogName,
  }) => {
    const { total, expires, packageType } =
      getProductInfoForUserPackagePurchase({
        user,
        packageState,
        voucher,
      });
    setProductInfo({ total, expires, packageType });
    handleOpenDialog(dialogName);
  };

  const handleChangeLoading = (value) => setLoading(value);

  const handleChangeIsPhoneNumberValid = (value) =>
    setIsPhoneNumberValid(value);

  const handleRedirectToPaymentHistory = () =>
    history.push(
      isDubbingRoute ? ROUTES.PAYMENT_HISTORY_DUBBING : ROUTES.PAYMENT_HISTORY,
    );
  const handleRedirectToPackage = () => {
    if (useNewPackagesTab) {
      if (packageState?.code?.includes(PACKAGE_TYPE.STUDIO)) {
        history.push(ROUTES.PAYMENT);
        return;
      }
      history.push(ROUTES.PAYMENT_DUBBING);
      return;
    }
    history.push(isDubbingRoute ? ROUTES.PAYMENT_DUBBING : ROUTES.PAYMENT);
  };

  const handleRedirectToTTS = () =>
    history.push(isDubbingRoute ? ROUTES.DUBBING : ROUTES.TTS);

  const checkNoneRedirectOnlineGateway = (paymentMethodType) => {
    const NONE_REDIRECT_ONLINE_GATEWAY = [PAYMENT_METHOD_TYPE.SHOPEEPAY];
    return paymentMethodType.includes(NONE_REDIRECT_ONLINE_GATEWAY);
  };

  const getBankDefault = () => {
    const bankDefaultName = getDefaultBank(bankDefaultValue);
    const bankDefault = banks.find((bank) => bank.name === bankDefaultName);

    return bankDefault;
  };

  const calculateCharacterPurchase = useMemo(() => {
    const { volumeDiscount, creditsPerUnit, vndPricePerUnit, usdPricePerUnit } =
      packageState || {};

    const { discountRatePerStep, creditsPerDiscountStep, maxDiscountRate } =
      volumeDiscount || {};

    const steps = Math.floor(currentNumberCharacters / creditsPerDiscountStep);
    const discountRate = Math.min(steps * discountRatePerStep, maxDiscountRate);
    const packagePrice =
      (currentNumberCharacters / creditsPerUnit) * vndPricePerUnit;
    const usdPackagePrice =
      (currentNumberCharacters / creditsPerUnit) * usdPricePerUnit;

    let customPrice = packagePrice * (1 - discountRate);
    let customUsdPrice = usdPackagePrice * (1 - discountRate);

    customPrice = Math.round(customPrice / 1000) * 1000;
    customUsdPrice = Math.round((customUsdPrice + 0.000001) * 100) / 100; // + 0.000001 to fix the floating point issue in JS

    return {
      packagePrice,
      usdPackagePrice,
      customPrice,
      customUsdPrice,
      discountRate,
    };
  }, [packageState, currentNumberCharacters]);

  const { price: currPrice, usdPrice: currUsdPrice } = getPackageCurrentPrice(
    packageState,
    true,
  );

  const customPackagePrice = useMemo(() => {
    const { customResource } = packageState || {};
    const { credits, devices, voiceClonings } = customResource || {};
    const calculateCustomPackagePrice = getCustomPackagePrice({
      credits: +creditsQuery || credits?.min,
      devices: +devicesQuery || devices?.min,
      voiceClonings: +voiceCloningsQuery || voiceClonings?.min,
      customResource,
      isVietNam,
    });

    return calculateCustomPackagePrice;
  }, [packageState, isVietNam, creditsQuery, devicesQuery, voiceCloningsQuery]);

  const savedPrice = useMemo(() => {
    const { price: remainingPrice = 0, usdPrice: remainingUsdPrice = 0 } =
      remainingValueOfTheCurrentPackage || {};

    const currentPrice = isCustomPackage
      ? customPackagePrice?.price
      : currPrice;
    const currentUsdPrice = isCustomPackage
      ? customPackagePrice?.price
      : currUsdPrice;

    const adjustedPrice = Math.max(0, currentPrice - remainingPrice);
    const adjustedUsdPrice = Math.max(0, currentUsdPrice - remainingUsdPrice);

    return getSavedPrice({
      voucher,
      currPrice: adjustedPrice,
      currUsdPrice: adjustedUsdPrice,
    });
  }, [
    customPackagePrice,
    isCustomPackage,
    voucher,
    currPrice,
    currUsdPrice,
    remainingValueOfTheCurrentPackage,
  ]);

  const totalPrice = getTotalPrice({
    voucher,
    currPrice,
    currUsdPrice,
    savedPrice,
  });
  const totalPriceTopUpPack = isVietNam
    ? calculateCharacterPurchase?.customPrice
    : calculateCharacterPurchase?.customUsdPrice;

  const verifyInvoice = () => {
    const { companyName, companyAddress, companyTax, companyEmail } = invoice;
    const errors = {};

    if (!companyName.trim()) errors.companyName = 'companyNameRequired';
    if (companyName?.trim() && companyName?.trim().length < 2)
      errors.companyName = 'companyNameInvalid';
    if (!companyAddress?.trim())
      errors.companyAddress = 'companyAddressRequired';
    if (!companyTax?.trim()) errors.companyTax = 'companyTaxRequired';
    if (!companyEmail?.trim()) errors.companyEmail = 'emailRequired';
    if (companyEmail && !validateEmail(companyEmail))
      errors.companyEmail = 'emailInvalid';

    return errors;
  };

  const getCallbackUrl = () => {
    if (isDubbingRoute) return `${CRM_URL}${ROUTES.PAYMENT_DUBBING}`;
    if (isAutoRenew) return `${CRM_URL}${ROUTES.TTS}`;
    return `${CRM_URL}${ROUTES.PAYMENT}`;
  };

  const typeCheckUpDownGradePackV3 = () => {
    let type = '';
    if (Object.values(PACKAGE_CODE_V3)?.includes(user?.packageCode)) {
      const checkExpiredDate = (expiryDate) => {
        if (!expiryDate) return false;
        return moment().isAfter(expiryDate);
      };

      const isExpiredPackage = checkExpiredDate(user?.packageExpiryDate);
      if (isExpiredPackage) return null;

      const hasSamePackage = packageCode === user?.packageCode;

      if (hasSamePackage) return TYPE_POPUP_MIGRATE.TYPE_V3_SAME_LEVEL;

      type = TYPE_POPUP_MIGRATE.TYPE_V3_UP_DOWN_GRADE;
    } else {
      const lockCharacters = user?.lockCharacters;
      if (FREE_PACKAGE_CODES.includes(usingPackage?.code) && !lockCharacters) {
        if (isCustomPackage) return TYPE_POPUP_MIGRATE.TYPE_MIGRATE_V1_V2_TO_V3;
        return null;
      }
      type = TYPE_POPUP_MIGRATE.TYPE_MIGRATE_V1_V2_TO_V3;
    }
    return type;
  };

  const handleCheckExceedsCharacters = () => {
    const purchasedTopUpCredits =
      ttsUser?.studio?.cycle?.purchasedTopUpCredits || 0;
    const maxCreditsAllowed = packageState?.maxAdditionalCredits;

    const isExceeding =
      purchasedTopUpCredits + currentNumberCharacters > maxCreditsAllowed;

    if (isExceeding) {
      handleOpenDialog(DIALOG_NAMES.EXCEEDS_CHARACTERS);
    }

    return isExceeding;
  };

  const fetchUsingOrders = async () => {
    const data = await apis.orders.getUsingOrders();
    if (data && data.status) {
      setUsingOrders(data.result);
    }
  };

  useEffect(() => {
    fetchUsingOrders();
  }, [usingPackage]);

  const checkVoiceCloningQuotaForPlan = async () => {
    const shouldValidate = getFeatureValue(
      FEATURE_KEYS.VALIDATE_VOICE_CLONING_ON_PLAN_CHANGE,
      {
        userId: user.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
        packageCode: packageState?.code,
      },
    );

    if (!shouldValidate) return true;

    const isCustomPackage = checkCustomPackage({
      type: packageState.type,
      level: packageState.level,
    });

    // If package is custom, maxCloningVoices is the value from the parameter
    // Otherwise, it is the value from the package state
    let maxCloningVoices = isCustomPackage
      ? voiceCloningsQuery || 0
      : packageState?.maxCloningVoices || 0;

    const currentVoiceCloningCount = await apis.voiceCloning.getMyVoicesCount();

    const isVoiceCloningWhitelisted = getFeatureValue(
      FEATURE_KEYS.VOICE_CLONING,
      {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
      },
    );

    setVoiceCloningQuota({
      maxCloningVoices,
      currentVoiceCloningCount,
    });

    if (isVoiceCloningWhitelisted)
      maxCloningVoices = Math.max(
        maxCloningVoices,
        DEFAULT_VOICE_CLONING_LIMIT_FOR_WHITELIST,
      );

    return currentVoiceCloningCount <= maxCloningVoices;
  };

  const handleCreateOrder = async ({
    confirmDowngrade = false,
    confirmCancelSubscription = false,
    bankCode,
    confirmUpgradeOrBuyMore = false,
    confirmPaymentDialog = false,
    confirmUpDownGradeDialog = false,
  }) => {
    const isValidVoiceCloning = await checkVoiceCloningQuotaForPlan();
    const shouldShowVoiceCloningDialog =
      !isValidVoiceCloning && !isUIBuyTopupCharacters;

    if (shouldShowVoiceCloningDialog) {
      handleOpenDialog(DIALOG_NAMES.VOICE_CLONING_QUOTA);
      return;
    }

    if (voucher) {
      const { type } = voucher;
      if (useVoucherByRegion) {
        const country = isVietNam ? VOUCHER_REGION.VN : VOUCHER_REGION.NON_VN;
        const isValidVoucher = validateVoucherByRegion({ voucher, country });
        if (!isValidVoucher) {
          handleOpenDialog(DIALOG_NAMES.INVALID_REGION_VOUCHER);
          return;
        }
      } else if (useNewGlobalOrder && type && type === VOUCHER_TYPE.PRICE) {
        handleOpenDialog(DIALOG_NAMES.INVALID_REGION_VOUCHER);
        return;
      }
    }

    if (!user?.email && isAutoRenew) {
      handleOpenDialog(DIALOG_NAMES.WARNING_EMAIL_ERROR);
      return;
    }

    if (hasInvoice) {
      const invoiceError = verifyInvoice();
      if (Object.values(invoiceError).length > 0) {
        dispatch(actions.order.updateErrorInvoice(invoiceError));
        return;
      }
    }
    if (!userOrder.phoneNumber) {
      setIsPhoneNumberValid(false);
      // Focus to phone number input field when phone number is empty
      const inputPhoneNumber = document.getElementById(
        'input-phone-number-payment',
      );

      if (inputPhoneNumber) {
        inputPhoneNumber.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    const latestOrderData = isDubbingRoute
      ? latestDubbingOrder
      : usingOrders?.find((order) => order?.type === PACKAGE_TYPE.STUDIO);

    const isBuyOnApp = [
      PAYMENT_METHOD_TYPE.APPLE,
      PAYMENT_METHOD_TYPE.GOOGLE,
    ].includes(latestOrderData?.paymentMethod?.type);

    if (
      subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE &&
      isBuyOnApp &&
      !isUIBuyTopupCharacters
    ) {
      handleOpenDialog(DIALOG_NAMES.BUY_SUBSCRIPTION_ON_APP);
      return;
    }

    const isActiveAutoRenew =
      subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE &&
      !packageState?.isAutoRenew &&
      !confirmCancelSubscription;

    if (
      typeCheckUpDownGradePackV3() &&
      usePricingModelV3 &&
      !confirmUpDownGradeDialog &&
      !isUIBuyTopupCharacters &&
      !isActiveAutoRenew
    ) {
      handleOpenDialog(DIALOG_NAMES.UPGRADE_DOWNGRADE_PACK_V3);
      return;
    }
    let isExceedsCharacters = false;
    if (isUIBuyTopupCharacters) {
      isExceedsCharacters = handleCheckExceedsCharacters();
    }
    if (isExceedsCharacters && isUIBuyTopupCharacters) return;
    if (
      isUIBuyTopupCharacters &&
      !confirmPaymentDialog &&
      !isConfirmPaymentLocal
    ) {
      handleOpenDialog(DIALOG_NAMES.CONFIRM_PAYMENT);
      return;
    }

    const comparisonUsingPackage = isDubbingRoute
      ? usingDubbingPackage
      : usingPackage;
    const checkLatestOrderPending = isDubbingRoute
      ? latestDubbingOrder.status !== ORDER_STATUS.PENDING
      : latestOrder.status !== ORDER_STATUS.PENDING;

    const isDowngradePackage =
      comparisonUsingPackage &&
      comparisonUsingPackage?.rank > packageState.rank &&
      checkLatestOrderPending;

    const SHOULD_CANCEL_SUBSCRIPTION_STATUS = [
      SUBSCRIPTION_PURCHASE_STATUS.ACTIVE,
      SUBSCRIPTION_PURCHASE_STATUS.PENDING,
    ];

    const isOldPackageInvalid =
      (user?.remainingCharacters === 0 ||
        (comparisonUsingPackage?.packageExpiryDate &&
          moment().isAfter(comparisonUsingPackage?.packageExpiryDate))) &&
      packageState?.code &&
      packageState?.code.includes('V3');

    const shouldCancelSubscription =
      packageState.type === PACKAGE_TYPE.STUDIO &&
      SHOULD_CANCEL_SUBSCRIPTION_STATUS.includes(
        subscriptionPurchase?.status,
      ) &&
      !confirmCancelSubscription;

    if (
      !isOldPackageInvalid &&
      isDowngradePackage &&
      !confirmDowngrade &&
      !isUIBuyTopupCharacters &&
      !packageState?.cycle
    ) {
      if (shouldCancelSubscription) {
        handleOpenDialog(
          DIALOG_NAMES.DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION,
        );
      } else if (useNewWarningBuyPackage) {
        handleOpenConfirmDialog({
          user,
          packageState,
          voucher,
          dialogName: DIALOG_NAMES.DOWNGRADE_PACKAGE,
        });
      } else {
        handleOpenDialog(DIALOG_NAMES.DOWNGRADE_PACKAGE);
      }
      return;
    }

    if (
      SHOULD_CANCEL_SUBSCRIPTION_STATUS.includes(
        subscriptionPurchase?.status,
      ) &&
      !packageState?.isAutoRenew &&
      !isUIBuyTopupCharacters
    ) {
      handleOpenDialog(DIALOG_NAMES.CANCEL_SUBSCRIPTION);
      return;
    }

    if (
      !confirmUpgradeOrBuyMore &&
      useNewWarningBuyPackage &&
      !usePricingModelV3 &&
      !isUIBuyTopupCharacters
    ) {
      handleOpenConfirmDialog({
        user,
        packageState,
        voucher,
        dialogName: DIALOG_NAMES.UPGRADE_PACKAGE_OR_BUY_MORE,
      });
      return;
    }

    const bankDefault = getBankDefault();

    setLoading(true);
    const permateMarketerIdData =
      permateMarketerId ||
      permateMarketerIdFromLocalStorage ||
      permateMarketerIdFromCookie;
    const permateOfferIdData =
      permateOfferId ||
      permateOfferIdFromLocalStorage ||
      permateOfferIdFromCookie;

    const createFields = {
      ...(isUIBuyTopupCharacters ? { credits: currentNumberCharacters } : {}),
      packageId: packageState.id,
      isAutoRenewing: packageState?.isAutoRenew,
      paymentMethodId: paymentMethod.id,
      bankCode: bankCode || bankDefault?.bankNumber,
      callbackUrl: getCallbackUrl(),
      downgradePackage: confirmDowngrade,
      confirmCancelSubscription,
      phoneNumber: userOrder.phoneNumber,
      voucherId: voucher?.id,
      isGlobal: !isVietNam,
      affiliateMarketerId: user.affiliateMarketerId,
      dialCode: usePhoneCountryInput ? userOrder?.dialCode : undefined,
      permateMarketerId:
        usePermateTracking && permateMarketerIdData
          ? permateMarketerIdData
          : undefined,
      permateOfferId:
        usePermateTracking && permateOfferIdData
          ? permateOfferIdData
          : undefined,
    };
    if (isCustomPackage) {
      createFields.custom = {
        maxCloningVoices:
          +voiceCloningsQuery || customResource?.voiceClonings?.min,
        device: +devicesQuery || customResource?.devices?.min,
        credits: +creditsQuery || customResource?.credits?.min,
      };
    }
    if (hasInvoice) createFields.invoice = invoice;
    if (sendDataSensesFromServer)
      createFields.datasenses = dataSenses.getDataSensesRequireFields();

    const data = await apis.orders.createOrder(createFields);
    dispatch(actions.order.updateErrorInvoice({}));
    setLoading(false);

    if (!data?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'buyPackageFailure',
        }),
      );
      return;
    }

    const newOrder = data.result;
    const isLockCredits =
      (((!!packageState.cycle || isCustomPackage) && isUpgradePackage) ||
        isUsingCustomPackage) &&
      !packageState.isTopUp;
    if (isLockCredits) dispatch(actions.user.lockCycleTopUpCredits(true));

    dataSenses.sendAddToCartEvent({
      userId: newOrder.userId,
      product: newOrder.type,
      packageCode: newOrder.package?.code,
      price: newOrder.price,
      packagePrice: newOrder.packagePrice,
      packageId: newOrder.id,
      paymentMethod: newOrder.paymentMethod?.type,
    });

    const isRenderedBankTransfer =
      newOrder.paymentMethod?.type === PAYMENT_METHOD_TYPE.BANK &&
      !newOrder.paymentLink;

    if (isRenderedBankTransfer) {
      setBankTransferDialog({
        selectedBank: newOrder.bank,
        price: newOrder.price,
      });
      if (newOrder.type === PACKAGE_TYPE.STUDIO)
        dispatch(actions.user.getLatestOrderSuccess(newOrder));
      if (newOrder.type === PACKAGE_TYPE.DUBBING)
        dispatch(actions.user.getLatestDubbingOrderSuccess(newOrder));

      dispatch(actions.user.getLatestOrderSuccessByUser(newOrder));
      return;
    }

    if (checkNoneRedirectOnlineGateway(newOrder.paymentMethod?.type)) {
      setQRPaymentDialog({
        name: newOrder.paymentMethod?.name,
        icon: newOrder.paymentMethod?.icon,
        paymentLink: newOrder.paymentLink,
        price: newOrder.price,
        createdAt: newOrder.createdAt,
      });
      if (newOrder.type === PACKAGE_TYPE.STUDIO)
        dispatch(actions.user.getLatestOrderSuccess(newOrder));
      if (newOrder.type === PACKAGE_TYPE.DUBBING)
        dispatch(actions.user.getLatestDubbingOrderSuccess(newOrder));

      dispatch(actions.user.getLatestOrderSuccessByUser(newOrder));
      return;
    }
    window.location.href = data.result.paymentLink;
  };

  const handleContinuePaying = () => {
    handleCloseDialog(DIALOG_NAMES.UNPAID_ORDER);

    const latestOrderData = isDubbingRoute ? latestDubbingOrder : latestOrder;

    if (checkNoneRedirectOnlineGateway(latestOrderData?.paymentMethod?.type)) {
      setQRPaymentDialog({
        name: latestOrderData?.paymentMethod?.name,
        icon: latestOrderData?.paymentMethod?.icon,
        paymentLink: latestOrderData?.paymentLink,
        price: latestOrderData?.price,
        createdAt: latestOrderData?.createdAt,
      });
      return;
    }

    const isRenderedBankTransfer =
      latestOrderData.paymentMethod?.type === PAYMENT_METHOD_TYPE.BANK &&
      !latestOrderData.paymentLink;

    if (isRenderedBankTransfer) {
      setBankTransferDialog({
        selectedBank: latestOrderData.bank,
        price: latestOrderData?.price,
      });
      return;
    }

    if (latestOrderData.paymentLink)
      window.location.assign(latestOrderData.paymentLink);
  };

  const handleConfirmDowngradePackageAndCancelSubscription = async () => {
    handleCloseDialog(DIALOG_NAMES.DOWNGRADE_PACKAGE_AND_CANCEL_SUBSCRIPTION);
    handleCreateOrder({
      confirmDowngrade: true,
      confirmCancelSubscription: true,
      confirmUpgradeOrBuyMore: true,
    });
  };

  const handleConfirmCancelSubscription = async () => {
    handleCloseDialog(DIALOG_NAMES.CANCEL_SUBSCRIPTION);
    handleCreateOrder({
      confirmCancelSubscription: true,
      confirmUpgradeOrBuyMore: true,
    });
  };

  const handleConfirmPaymentTopup = async () => {
    await handleCreateOrder({
      confirmPaymentDialog: true,
    });
    handleCloseDialog(DIALOG_NAMES.CONFIRM_PAYMENT);
  };

  const handleConfirmUpDownGradePack = async () => {
    await handleCreateOrder({
      confirmDowngrade: true,
      confirmUpDownGradeDialog: true,
      confirmCancelSubscription:
        subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE &&
        packageState?.isAutoRenew,
    });
    handleCloseDialog(DIALOG_NAMES.UPGRADE_DOWNGRADE_PACK_V3);
  };

  const handleConfirmDowngradePackage = () => {
    handleCreateOrder({
      confirmDowngrade: true,
      confirmUpgradeOrBuyMore: true,
    });
    handleCloseDialog(DIALOG_NAMES.DOWNGRADE_PACKAGE);
  };

  const handleConfirmUpgradeOrBuyMore = () => {
    handleCreateOrder({
      confirmUpgradeOrBuyMore: true,
    });
    handleCloseDialog(DIALOG_NAMES.UPGRADE_PACKAGE_OR_BUY_MORE);
  };

  const handleOpenUnpaidOrderDialog = () => {
    setKeyDescriptionUnPaidOrder('descriptionPendingOrder');
    handleOpenDialog(DIALOG_NAMES.UNPAID_ORDER);
  };

  const handleCloseBankTransferDialog = () => setBankTransferDialog(false);

  const handleCloseQrDialog = () => setQRPaymentDialog(false);

  const fetchOrderState = async (token, packageCode) => {
    const data = await apis.orders.getOrderState(token);

    if (data.status) {
      switch (data.result.state) {
        case ORDER_STATE.SUCCESS: {
          handleOpenDialog(DIALOG_NAMES.COMPLETED_ORDER);
          const packageName = getPackageName(
            null,
            packageCode,
            language,
            t,
            true,
          );
          dispatch(
            actions.banner.updateDisplayBanner(
              BANNER_TYPE.JOIN_ZALO_GROUP,
              true,
              { packageName: packageName.toUpperCase() },
            ),
          );
          break;
        }
        case ORDER_STATE.FAILED:
          // TODO: handle Failed Dialog
          break;
        case ORDER_STATE.PROCESSING:
          // TODO: handle processing dialog
          break;
        default:
          break;
      }
    }
  };

  const fetchOrder = async (orderId) => {
    const data = await apis.orders.getOrderById(orderId);

    if (data.status) {
      handleChangeSelectedPackageCode(data.result.package.code);
      dispatch(actions.order.updatePackage(data.result.package));
      // TODO: Add voucher

      if (isVietNam) {
        dispatch(actions.order.updatePaymentMethod(data.result.paymentMethod));
      }
    }
  };

  const handleConfirmPaymentDialogCheckbox = () => {
    if (!isVisiblePaymentDialog && !isConfirmPaymentLocal)
      localStorage.setItem('confirmPaymentDialog', true);
    setIsVisiblePaymentDialog((value) => !value);
  };

  const handleChangeQRCode = async (currBankNumber) => {
    setLoading(true);
    const otherBank = banks.find((bank) => bank.bankNumber !== currBankNumber);

    await handleCreateOrder({
      confirmPaymentDialog: true,
      confirmUpDownGradeDialog: true,
      confirmDowngrade: true,
      confirmCancelSubscription: true,
      bankCode: otherBank?.bankNumber,
      confirmUpgradeOrBuyMore: true,
    });
    setLoading(false);
  };

  const checkPendingOrder = (latestOrderData) => {
    if (latestOrderData?.status === ORDER_STATUS.PENDING) {
      if (latestOrderData?.isConfirmedByCustomer && showPendingOrder) {
        handleOpenDialog(DIALOG_NAMES.NOT_CONFIRMED_ORDER);
        return;
      }
      if (showPendingOrder) handleOpenDialog(DIALOG_NAMES.UNPAID_ORDER);
    } else if (latestOrderData?.status) setShowPendingOrder(false);
  };

  const totalPriceV3 = useMemo(() => {
    const currentPackageValue =
      isUpgradePackage || isUsingCustomPackage
        ? remainingValueOfTheCurrentPackage
        : { price: 0, usdPrice: 0 };

    let basePrice = price || packageState.price;
    let baseUsdPrice = usdPrice || packageState.usdPrice;
    const isCustomPackage = checkCustomPackage({
      type: packageState.type,
      level: packageState.level,
    });
    if (isCustomPackage) {
      basePrice = customPackagePrice?.price;
      baseUsdPrice = customPackagePrice?.price;
    }

    const remainingPrice = basePrice - currentPackageValue?.price;
    const remainingUsdPrice = baseUsdPrice - currentPackageValue?.usdPrice;

    const minPaymentAmount = paymentMethod?.minPaymentAmount;
    const isAutoRenewEnabled = packageState?.isAutoRenew;

    const totalPriceRemaining = getTotalPrice({
      voucher,
      currPrice: remainingPrice,
      currUsdPrice: remainingUsdPrice,
      savedPrice,
    });

    let finalPrice = totalPriceRemaining;
    const isMinPaymentAmount =
      (useMinPaymentMethodAmount || isAutoRenewEnabled) && minPaymentAmount;
    if (isMinPaymentAmount) {
      const minAmount = isVietNam
        ? minPaymentAmount?.vnd
        : minPaymentAmount?.usd;
      finalPrice = Math.max(finalPrice, minAmount);
    }

    finalPrice = Math.max(0, finalPrice);

    return {
      totalPrice: usePricingModelV3 ? finalPrice : totalPrice,
      actualPrice: totalPriceRemaining,
    };
  }, [
    customPackagePrice,
    isUpgradePackage,
    price,
    usdPrice,
    voucher,
    isVietNam,
    packageState,
    remainingValueOfTheCurrentPackage,
    totalPrice,
    paymentMethod,
  ]);

  useEffect(() => {
    setCurrentNumberCharacters(
      min(packageState?.creditPurchaseOptions || []) || 0,
    );
  }, [packageState?.creditPurchaseOptions]);

  useEffect(() => {
    if (!isVietNam && location?.search?.includes(PREFIX_AUTO_RENEW)) {
      history.push(ROUTES.TTS);
    }
  }, []);

  useEffect(() => {
    if (isDubbingRoute) checkPendingOrder(latestDubbingOrder);
    else checkPendingOrder(latestOrder);
  }, [latestOrder, latestDubbingOrder, showPendingOrder]);

  useEffect(() => {
    if (!isVietNam && location?.search?.includes(PREFIX_AUTO_RENEW)) {
      history.push(ROUTES.TTS);
      return;
    }

    const {
      token,
      orderId,
      package: packageCode,
    } = camelcaseKeys(queryString.parse(location.search) || {});
    if (FREE_PACKAGE_CODES.includes(packageCode)) history.push(ROUTES.PAYMENT);

    if (token) fetchOrderState(token, packageCode);

    if (orderId) fetchOrder(orderId);
  }, [location]);

  useEffect(() => {
    if (backToTTS) {
      dispatch(actions.user.resetBackToTTS());
      history.push(ROUTES.TTS);
    }

    // Redirect to dubbing page after payment success not trial package
    if (
      backToDubbing &&
      user?.dubbing?.packageCode &&
      user?.dubbing?.packageCode !== PACKAGE_LEVEL.DUBBING_TRIAL
    ) {
      dispatch(actions.user.resetBackToDubbing());
      history.push(ROUTES.HOME_DUBBING);
    }
  }, [backToTTS, backToDubbing, user?.dubbing]);

  const getRemainingValueOfTheCurrentPackage = async (packageType) => {
    const data = await apis.orders.remainingValueOfTheCurrentPackage({
      packageType,
      device: devicesQuery,
      maxCloningVoices: voiceCloningsQuery,
    });

    if (data?.status) setRemainingValueOfTheCurrentPackage(data?.result);
  };

  useEffect(() => {
    // Get remaining value of the current package when using pricing model V3, not buy topup characters, upgrade package
    const checkConditionGetRemainingValue =
      usePricingModelV3 &&
      !isUIBuyTopupCharacters &&
      refundPriceCurrPlan &&
      (isUpgradePackage || isUsingCustomPackage);

    if (checkConditionGetRemainingValue)
      getRemainingValueOfTheCurrentPackage(
        location.pathname.includes('studio')
          ? PACKAGE_TYPE.STUDIO
          : PACKAGE_TYPE.API,
      );
    else setRemainingValueOfTheCurrentPackage({ price: 0, usdPrice: 0 });
  }, [
    isUpgradePackage,
    location,
    usePricingModelV3,
    isUIBuyTopupCharacters,
    refundPriceCurrPlan,
    devicesQuery,
    voiceCloningsQuery,
  ]);

  useEffect(() => {
    if (closeQRPayment) {
      dispatch(actions.user.resetCloseQRPayment());
      handleCloseBankTransferDialog();
      handleCloseQrDialog();
    }
  }, [closeQRPayment]);

  useEffect(() => {
    fetchBanks();
  }, []);

  // useEffect(() => {
  //   if (Object.keys(packageState).length === 0 && totalPrice) return;
  //   dataSenses.sendViewPaymentPageEvent({
  //     userId: user.id,
  //     category: packageState.type,
  //     name: packageState.code,
  //     packagePrice: packageState.price,
  //     price: totalPrice,
  //     voucherCode: voucher?.code,
  //   });
  // }, [location.search, totalPrice]);

  // TODO: Refactor so that can pass currentProduct to Order info instead of duplicate code like this
  const currentProduct = useMemo(() => {
    const packageTypes = [
      {
        label: useRenameMergeProduct ? t('AIVOICE_STUDIO') : t('studioPackage'),
        id: PACKAGE_TYPE.STUDIO,
      },
      { label: t('DUBBING'), id: PACKAGE_TYPE.DUBBING },
    ];
    const selectLabel = packageTypes?.find(
      (item) => item?.id === packageState?.type,
    )?.label;
    return selectLabel;
  }, [packageState, language]);

  const isReplaceHistory = useMemo(() => {
    if (!location.pathname.includes('studio')) return false;

    const usingStudioOrder = usingOrders?.find(
      (order) => order?.id === user?.orderId,
    );

    const hasAutoRenewInOrder =
      usingStudioOrder?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
    const hasSamePackage = usingPackage?.code === packageCode;
    const activeAutoRenew =
      subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;

    if (
      (hasAutoRenewInOrder &&
        hasSamePackage &&
        isAutoRenew &&
        activeAutoRenew) ||
      isAutoRenew === null
    ) {
      return true;
    }

    if (isAutoRenew && packages?.length > 0) {
      return !packages?.some((pkg) => pkg?.isAutoRenew);
    }

    const isExpiredPackage = user?.packageExpiryDate
      ? moment().isAfter(user.packageExpiryDate)
      : false;

    if (!TOP_UP_PACKAGE_CODES.includes(packageCode)) return false;

    const isTopUpPackage =
      isUIBuyTopupCharacters &&
      usingPackage?.topUpPackageCode === packageCode &&
      !isExpiredPackage;
    return !isTopUpPackage;
  }, [
    usingOrders,
    packages,
    isAutoRenew,
    location.pathname,
    packageCode,
    isUIBuyTopupCharacters,
    user,
    usingPackage,
    subscriptionPurchase,
  ]);

  if (isReplaceHistory && !!usingPackage?.id) {
    history.replace(location.pathname);
  }

  if (isReplaceHistory) {
    return <></>;
  }
  return (
    <>
      <Backdrop sx={{ color: COLOR.white, zIndex: '9999999' }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <StyledPayment
        useNewLegal={useNewLegal}
        openSidebar={openSidebar}
        isAutoRenew={isAutoRenew}
        isUIBuyTopupCharacters={isUIBuyTopupCharacters}
      >
        <div className="header-payment">
          <Button
            variant="contained"
            startIcon={<KeyboardArrowLeft />}
            className="back-to-package icon website-button"
            onClick={handleRedirectToPackage}
            data-id="button-back-pricing"
            data-product={packageState?.type}
            data-package-code={packageState?.code}
          >
            {t('backToPackage')}
          </Button>
          <IconButton
            variant="contained"
            className="back-to-package icon medium-website-button"
            onClick={handleRedirectToPackage}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <KeyboardArrowLeft
            className="back-to-package-mobile mobile-button"
            onClick={handleRedirectToPackage}
          />

          {useNewPackageMobile ? (
            <Typography
              data-id="button-back-pricing"
              data-product={packageState?.type}
              data-package-code={packageState?.code}
              onClick={handleRedirectToPackage}
              className="title"
            >
              {t('backToPackage')}
            </Typography>
          ) : (
            <Typography className="title">
              {t('payment')} {currentProduct || ''}
            </Typography>
          )}
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} mlg={openSidebar ? 12 : 6} cmlg={6}>
            <Card className="card">
              <PackageInfo
                setPackages={setPackages}
                packages={packages}
                usingOrders={usingOrders}
                calculateCharacterPurchase={calculateCharacterPurchase}
                currentNumberCharacters={currentNumberCharacters}
                onChangeNumberCharacters={(value) =>
                  setCurrentNumberCharacters(value)
                }
                selectedPackageCode={selectedPackageCode}
                onChangeSelectedPackageCode={handleChangeSelectedPackageCode}
              />
              <PaymentMethods isAutoRenew={isAutoRenew} />
              <div className="show-mobile">
                <OrderInfo
                  actualPrice={
                    totalPriceV3?.actualPrice > 0
                      ? totalPriceV3?.actualPrice
                      : 0
                  }
                  remainingValueOfTheCurrentPackage={
                    remainingValueOfTheCurrentPackage
                  }
                  calculateCharacterPurchase={calculateCharacterPurchase}
                  currentNumberCharacters={currentNumberCharacters}
                  useNewGlobalOrder={useNewGlobalOrder}
                  useVoucherByRegion={useVoucherByRegion}
                  totalPrice={
                    isUIBuyTopupCharacters
                      ? totalPriceTopUpPack
                      : totalPriceV3?.totalPrice
                  }
                  isPhoneNumberValid={isPhoneNumberValid}
                  onChangePhoneNumberValid={handleChangeIsPhoneNumberValid}
                  isAutoRenew={isAutoRenew}
                  useMinPaymentMethodAmount={useMinPaymentMethodAmount}
                  customPackagePrice={customPackagePrice}
                />
              </div>
              {isAutoRenew && (
                <div className="show-mobile">
                  <SubscriptionInfo
                    buyPackage={packageState}
                    isAutoRenewalPackage={isAutoRenew}
                  />
                </div>
              )}
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card className="card show-desktop">
              <OrderInfo
                actualPrice={
                  totalPriceV3?.actualPrice > 0 ? totalPriceV3?.actualPrice : 0
                }
                remainingValueOfTheCurrentPackage={
                  remainingValueOfTheCurrentPackage
                }
                calculateCharacterPurchase={calculateCharacterPurchase}
                currentNumberCharacters={currentNumberCharacters}
                useNewGlobalOrder={useNewGlobalOrder}
                useVoucherByRegion={useVoucherByRegion}
                totalPrice={
                  isUIBuyTopupCharacters
                    ? totalPriceTopUpPack
                    : totalPriceV3?.totalPrice
                }
                isPhoneNumberValid={isPhoneNumberValid}
                onChangePhoneNumberValid={handleChangeIsPhoneNumberValid}
                isAutoRenewalPackage={isAutoRenew}
                useMinPaymentMethodAmount={useMinPaymentMethodAmount}
                customPackagePrice={customPackagePrice}
              />
            </Card>
            {isAutoRenew && (
              <Card className="card show-desktop subscription-wrapper">
                <SubscriptionInfo
                  buyPackage={packageState}
                  isAutoRenewalPackage={isAutoRenew}
                />
              </Card>
            )}
          </Grid>
        </Grid>
      </StyledPayment>
      <PaymentFooter
        actualPrice={
          totalPriceV3?.actualPrice > 0 ? totalPriceV3?.actualPrice : 0
        }
        isAutoRenew={isAutoRenew}
        totalPrice={
          isUIBuyTopupCharacters
            ? totalPriceTopUpPack
            : totalPriceV3?.totalPrice
        }
        onCreateOrder={handleCreateOrder}
      />
      {bankTransferDialog && (
        <BankTransferDialogContainer
          open
          selectedBank={bankTransferDialog?.selectedBank}
          totalPrice={getOrderPrice(
            isDubbingRoute ? latestDubbingOrder : latestOrder,
          )}
          onComeBackToPackage={handleRedirectToPackage}
          onRedirectToPaymentHistory={handleRedirectToPaymentHistory}
          onChangeLoading={handleChangeLoading}
          onCloseBankTransferDialog={handleCloseBankTransferDialog}
          isDubbingRoute={isDubbingRoute}
          onChangeQR={handleChangeQRCode}
          onOpenUnpaidOrderDialog={handleOpenUnpaidOrderDialog}
        />
      )}
      {qrPaymentDialog && (
        <QRPaymentDialog
          open
          paymentInfo={qrPaymentDialog}
          totalPrice={
            isUIBuyTopupCharacters
              ? totalPriceTopUpPack
              : totalPriceV3?.totalPrice
          }
          onChangeLoading={handleChangeLoading}
          onComeBackToPackage={handleRedirectToPackage}
          onCloseQrDialog={handleCloseQrDialog}
          isDubbingRoute={isDubbingRoute}
        />
      )}
      <PaymentNotiDialog
        typeCheckUpDownGradePackV3={typeCheckUpDownGradePackV3()}
        isAutoRenewing={packageState?.isAutoRenew}
        handleConfirmUpDownGradePack={handleConfirmUpDownGradePack}
        dataCharacterLimitExceeded={{
          maxCharacters:
            packageState?.maxAdditionalCredits?.toLocaleString(language),
          currentCharacters: (
            packageState?.maxAdditionalCredits -
            (ttsUser?.studio?.cycle?.purchasedTopUpCredits || 0)
          )?.toLocaleString(language),
        }}
        handleConfirmPaymentTopup={handleConfirmPaymentTopup}
        isVisiblePaymentDialog={isVisiblePaymentDialog}
        handleConfirmPaymentDialogCheckbox={handleConfirmPaymentDialogCheckbox}
        dataConfirmPaymentDialog={{
          numberCharacters: currentNumberCharacters.toLocaleString(language),
          dateExpired: moment(ttsUser?.studio?.cycle?.nextResetAt).format(
            'DD/MM/YYYY',
          ),
          hour: moment(ttsUser?.studio?.cycle?.nextResetAt).format('HH:mm'),
          remainingDays: moment(ttsUser?.studio?.cycle?.nextResetAt).diff(
            moment(),
            'days',
          ),
        }}
        voiceCloningQuota={voiceCloningQuota}
        DIALOG_NAMES={DIALOG_NAMES}
        openDialog={openDialog}
        onCloseDialog={handleCloseDialog}
        onChangeLoading={handleChangeLoading}
        onComeBackToPackage={handleRedirectToPackage}
        onConfirmDowngradePackage={handleConfirmDowngradePackage}
        onConfirmDowngradePackageAndCancelSubscription={
          handleConfirmDowngradePackageAndCancelSubscription
        }
        onConfirmUpgradeOrBuyMore={handleConfirmUpgradeOrBuyMore}
        onConfirmCancelSubscription={handleConfirmCancelSubscription}
        onContinuePaying={handleContinuePaying}
        onRedirectToTTS={handleRedirectToTTS}
        useNewWarningBuyPackage={useNewWarningBuyPackage}
        isDubbingRoute={isDubbingRoute}
        productInfo={productInfo}
        paymentMethodType={
          isDubbingRoute
            ? latestDubbingOrder?.paymentMethod?.type
            : usingOrders?.find((order) => order?.type === PACKAGE_TYPE.STUDIO)
                ?.paymentMethod?.type
        }
        keyDescriptionUnPaidOrder={keyDescriptionUnPaidOrder}
      />
    </>
  );
};

export default Payment;
